import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PlusOutlined, MinusOutlined, CompressOutlined } from '@ant-design/icons';

import { PlanNode, TrackCode } from '../../../TraineeDashboard/DevelopmentPlan/types';
import {
  Edge,
  TrackHeading,
  trackHeadings,
  trackOrient,
  WORLD_CENTER,
  WORLD_SIZE,
  XY,
} from '../../../TraineeDashboard/DevelopmentPlan/helpers';
import SkillEdges from '../../../TraineeDashboard/DevelopmentPlan/components/SkillEdges';
import EditableNodeCard from './EditableNodeCard';

type Props = {
  nodes: PlanNode[];
  byCode: Record<string, PlanNode>;
  positions: Record<string, XY>;
  edges: Edge[];
  trackColor: Record<string, string>;
  trackCodes: string[]; // tracks in this plan's supported cross profile
  selectedCode: string | null;
  issueCodes: Set<string>;
  inactiveCodes: Set<string>;
  editable: boolean;
  onSelectNode: (code: string) => void;
  onSelectTrack: (trackCode: string) => void;
  onBackground: () => void;
  onMoveNodeToTrack: (code: string, trackCode: string) => void;
  onCreateEdge: (fromCode: string, toCode: string) => void;
};

const ZOOM_MIN = 0.2;
const ZOOM_MAX = 1.8;
const ZOOM_INIT = 1.1;
const DRAG_THRESHOLD = 4;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const TRACK_LABEL: Record<string, string> = {
  trader: 'Trader',
  'idea-generator': 'Idea Gen',
  'risk-manager': 'Risk',
  quant: 'Quant',
  'vol-academy': 'Vol Academy',
};

const ARROW = (h: TrackHeading) =>
  h.orient === 'h' ? (h.dir > 0 ? '→' : '←') : h.dir > 0 ? '↓' : '↑';

/** Which track's ARM a world-point falls into — the inverse of the layout's
 *  heading map, so a dragged node lands on the track it visually sits under.
 *  Null when that arm is empty for this plan (e.g. "down" on a quant-less tree),
 *  which the callers already treat as "not a move target". */
function armTrack(
  wx: number,
  wy: number,
  headings: Record<TrackCode, TrackHeading>,
  codes: string[]
): TrackCode | null {
  const orient: 'v' | 'h' = Math.abs(wx) > Math.abs(wy) ? 'h' : 'v';
  const dir = (orient === 'h' ? wx : wy) > 0 ? 1 : -1;
  const hit = codes.find((c) => {
    const h = headings[c as TrackCode];
    return h && h.orient === orient && h.dir === dir;
  });
  return (hit as TrackCode) ?? null;
}

/**
 * Edit-mode tree canvas. Forks the trainee SkillTreeCanvas: keeps its pan/zoom
 * viewport and reuses SkillEdges for edge rendering, but replaces node interaction
 * with editor gestures — click to select, drag to reassign a node's track (by
 * quadrant), Shift+drag from node → node to draw a prerequisite edge. Layout
 * positions recompute upstream when track/group change, so a moved node re-lands.
 */
const EditableTreeCanvas: React.FC<Props> = ({
  nodes,
  byCode,
  positions,
  edges,
  trackColor,
  trackCodes,
  selectedCode,
  issueCodes,
  inactiveCodes,
  editable,
  onSelectNode,
  onSelectTrack,
  onBackground,
  onMoveNodeToTrack,
  onCreateEdge,
}) => {
  // Arm assignment for THIS plan (quant-less trees use a different cross shape).
  // Must match what `computeLayout` used upstream, or drags land on the wrong track.
  const headings = React.useMemo(
    () => trackHeadings(trackCodes.map((code) => ({ code }))),
    [trackCodes]
  );
  const dirLabel = React.useCallback(
    (code: string) =>
      headings[code as TrackCode]
        ? `${TRACK_LABEL[code] ?? code} ${ARROW(headings[code as TrackCode])}`
        : TRACK_LABEL[code] ?? code,
    [headings]
  );

  const vpRef = useRef<HTMLDivElement>(null);
  const [tf, setTf] = useState({ tx: 0, ty: 0, zoom: ZOOM_INIT });
  const [hoverCode, setHoverCode] = useState<string | null>(null);
  const [panning, setPanning] = useState(false);
  const [drawEdge, setDrawEdge] = useState<{ from: XY; to: XY } | null>(null);
  const [moveHint, setMoveHint] = useState<{ x: number; y: number; label: string } | null>(null);

  const pan = useRef<{ moved: boolean; x: number; y: number } | null>(null);
  const nodeAction = useRef<{
    code: string;
    x: number;
    y: number;
    moved: boolean;
    mode: 'move' | 'edge';
  } | null>(null);
  const tfRef = useRef(tf);
  tfRef.current = tf;
  const hoverRef = useRef<string | null>(null);
  hoverRef.current = hoverCode;
  const initialized = useRef(false);

  const recenter = useCallback((zoom: number) => {
    const vp = vpRef.current;
    if (!vp) return;
    setTf({
      zoom,
      tx: vp.clientWidth / 2 - WORLD_CENTER * zoom,
      ty: vp.clientHeight / 2 - WORLD_CENTER * zoom,
    });
  }, []);

  useEffect(() => {
    const vp = vpRef.current;
    if (!vp) return undefined;
    const ro = new ResizeObserver(() => {
      if (!initialized.current && vp.clientWidth > 0 && vp.clientHeight > 0) {
        initialized.current = true;
        recenter(ZOOM_INIT);
      }
    });
    ro.observe(vp);
    return () => ro.disconnect();
  }, [recenter]);

  // Non-passive wheel → zoom to cursor.
  useEffect(() => {
    const vp = vpRef.current;
    if (!vp) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = vp.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setTf((prev) => {
        const zoom = clamp(prev.zoom * Math.exp(-e.deltaY * 0.0015), ZOOM_MIN, ZOOM_MAX);
        const k = zoom / prev.zoom;
        return { zoom, tx: cx - (cx - prev.tx) * k, ty: cy - (cy - prev.ty) * k };
      });
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, []);

  const toWorld = useCallback((clientX: number, clientY: number): XY => {
    const vp = vpRef.current;
    const rect = vp ? vp.getBoundingClientRect() : ({ left: 0, top: 0 } as DOMRect);
    const t = tfRef.current;
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    return {
      x: (px - t.tx) / t.zoom - WORLD_CENTER,
      y: (py - t.ty) / t.zoom - WORLD_CENTER,
    };
  }, []);

  const toViewport = useCallback((clientX: number, clientY: number): XY => {
    const vp = vpRef.current;
    const rect = vp ? vp.getBoundingClientRect() : ({ left: 0, top: 0 } as DOMRect);
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  // Screen (viewport) position of a node center, for the rubber-band origin.
  const nodeViewport = useCallback((code: string): XY => {
    const p = positions[code] || { x: 0, y: 0 };
    const t = tfRef.current;
    return {
      x: t.tx + (WORLD_CENTER + p.x) * t.zoom,
      y: t.ty + (WORLD_CENTER + p.y) * t.zoom,
    };
  }, [positions]);

  // Global move/up handlers (pan + node drag + edge draw continue outside nodes).
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (pan.current) {
        const dx = e.clientX - pan.current.x;
        const dy = e.clientY - pan.current.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) pan.current.moved = true;
        pan.current.x = e.clientX;
        pan.current.y = e.clientY;
        setTf((prev) => ({ ...prev, tx: prev.tx + dx, ty: prev.ty + dy }));
        return;
      }
      const na = nodeAction.current;
      if (!na) return;
      if (Math.abs(e.clientX - na.x) + Math.abs(e.clientY - na.y) > DRAG_THRESHOLD) {
        na.moved = true;
      }
      if (!na.moved || !editable) return;
      if (na.mode === 'edge') {
        setDrawEdge({ from: nodeViewport(na.code), to: toViewport(e.clientX, e.clientY) });
      } else {
        const w = toWorld(e.clientX, e.clientY);
        const target = armTrack(w.x, w.y, headings, trackCodes);
        const vp = toViewport(e.clientX, e.clientY);
        setMoveHint(
          target && trackCodes.includes(target)
            ? { x: vp.x, y: vp.y, label: dirLabel(target) }
            : null
        );
      }
    };
    const onUp = (e: MouseEvent) => {
      if (pan.current) {
        const moved = pan.current.moved;
        pan.current = null;
        setPanning(false);
        if (!moved) onBackground();
        return;
      }
      const na = nodeAction.current;
      nodeAction.current = null;
      setDrawEdge(null);
      setMoveHint(null);
      if (!na) return;
      if (!na.moved) {
        onSelectNode(na.code);
        return;
      }
      if (!editable) return;
      if (na.mode === 'edge') {
        const target = hoverRef.current;
        if (target && target !== na.code) onCreateEdge(na.code, target);
      } else {
        const w = toWorld(e.clientX, e.clientY);
        const target = armTrack(w.x, w.y, headings, trackCodes);
        const current = byCode[na.code]?.track_code;
        if (target && trackCodes.includes(target) && target !== current) {
          onMoveNodeToTrack(na.code, target);
        }
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [
    editable,
    trackCodes,
    byCode,
    nodeViewport,
    toViewport,
    toWorld,
    onBackground,
    onSelectNode,
    onCreateEdge,
    onMoveNodeToTrack,
  ]);

  const onCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    pan.current = { moved: false, x: e.clientX, y: e.clientY };
    setPanning(true);
  };

  const onNodeMouseDown = (code: string, e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation(); // don't start a pan
    nodeAction.current = {
      code,
      x: e.clientX,
      y: e.clientY,
      moved: false,
      mode: editable && e.shiftKey ? 'edge' : 'move',
    };
  };

  const zoomBy = (factor: number) => {
    const vp = vpRef.current;
    if (!vp) return;
    const cx = vp.clientWidth / 2;
    const cy = vp.clientHeight / 2;
    setTf((prev) => {
      const zoom = clamp(prev.zoom * factor, ZOOM_MIN, ZOOM_MAX);
      const k = zoom / prev.zoom;
      return { zoom, tx: cx - (cx - prev.tx) * k, ty: cy - (cy - prev.ty) * k };
    });
  };

  return (
    <div
      ref={vpRef}
      className={`devadmin-canvas${panning ? ' is-panning' : ''}`}
      onMouseDown={onCanvasMouseDown}
    >
      <div
        className="devadmin-world"
        style={{
          width: WORLD_SIZE,
          height: WORLD_SIZE,
          transform: `translate(${tf.tx}px, ${tf.ty}px) scale(${tf.zoom})`,
        }}
      >
        <SkillEdges
          edges={edges}
          positions={positions}
          byCode={byCode}
          trackColor={trackColor as Record<TrackCode, string>}
          selectedId={selectedCode}
          hoveredId={hoverCode}
          focusTrack={null}
          orient={trackOrient(trackCodes.map((code) => ({ code })))}
        />

        <div
          className="devadmin-core"
          style={{ left: WORLD_CENTER, top: WORLD_CENTER }}
          aria-hidden
        />

        {nodes.map((node) => {
          const pos = positions[node.code];
          if (!pos) return null;
          return (
            <div
              key={node.code}
              className="devadmin-node-pos"
              style={{ left: WORLD_CENTER + pos.x, top: WORLD_CENTER + pos.y }}
            >
              <EditableNodeCard
                node={node}
                color={trackColor[node.track_code] ?? '#8aa'}
                selected={selectedCode === node.code}
                inactive={inactiveCodes.has(node.code)}
                hasIssue={issueCodes.has(node.code)}
                editable={editable}
                onNodeMouseDown={onNodeMouseDown}
                onEnter={setHoverCode}
                onLeave={() => setHoverCode(null)}
              />
            </div>
          );
        })}
      </div>

      {/* Rubber-band while drawing a prerequisite edge. */}
      {drawEdge && (
        <svg className="devadmin-rubber" width="100%" height="100%">
          <line
            x1={drawEdge.from.x}
            y1={drawEdge.from.y}
            x2={drawEdge.to.x}
            y2={drawEdge.to.y}
          />
        </svg>
      )}

      {moveHint && (
        <div
          className="devadmin-movehint"
          style={{ left: moveHint.x + 14, top: moveHint.y + 14 }}
        >
          → {moveHint.label}
        </div>
      )}

      <div className="devadmin-legend">
        {trackCodes.map((code) => (
          <button
            key={code}
            type="button"
            className="devadmin-legend__item"
            title="Edit track"
            onClick={() => onSelectTrack(code)}
          >
            <span
              className="devadmin-legend__swatch"
              style={{ background: trackColor[code] }}
            />
            {dirLabel(code)}
          </button>
        ))}
      </div>

      <div className="devadmin-zoomctl">
        <button type="button" onClick={() => zoomBy(1.2)} title="Zoom in">
          <PlusOutlined />
        </button>
        <button type="button" onClick={() => zoomBy(1 / 1.2)} title="Zoom out">
          <MinusOutlined />
        </button>
        <button type="button" onClick={() => recenter(ZOOM_INIT)} title="Fit">
          <CompressOutlined />
        </button>
      </div>

      <div className="devadmin-canvas-hint">
        {editable
          ? 'Drag node → move track · Shift+drag node → node → prerequisite · click → edit'
          : 'Published (read-only) — clone to edit'}
      </div>
    </div>
  );
};

export default EditableTreeCanvas;
